import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { Invoice, invoicesAPI } from '@/services/api/invoices';

interface ReopenModalProps {
  isOpen: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: (invoice: Invoice) => void;
}

const ReopenModal: React.FC<ReopenModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNote('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !invoice) return null;

  const canSubmit = note.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const updated = await invoicesAPI.reopen(invoice.id, note.trim());
      toast.success('Invoice reopened', {
        description: `${updated.invoiceNumber} is now marked received at hub.`,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      toast.error('Could not reopen invoice', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-arrow-go-back-line" />}
      iconTone="warning"
      title="Reopen and mark received"
      subtitle={`${invoice.invoiceNumber} · ${invoice.rider?.name ?? '—'}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Reopening…' : 'Reopen'}
          </Button>
        </div>
      }
    >
      <div>
        <label
          htmlFor="reopen-note"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
        >
          Note <span className="text-error-base">*</span>
        </label>
        <Textarea
          id="reopen-note"
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="How was the doc recovered? e.g. rider found it in the van, reprinted at outlet."
        />
        <p className="mt-2 text-xs text-text-sub-600">
          This moves the invoice straight to{' '}
          <span className="font-semibold">Received at hub</span> — no re-scan
          needed. Previous exception note is replaced; full history stays in
          the audit trail.
        </p>
      </div>
    </FormModal>
  );
};

export default ReopenModal;
